import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect, ConnectedProps } from 'react-redux';

import { AppState } from '../../stores/redux-store';
import { messages as authorizationMessages } from '../AuthorizationDirector/messages';
import { LoadingOverlay } from '../../core/screens/LoadingOverlay/LoadingOverlay';
import { SuccessNotification } from '../../core/screens/SuccessNotification/SuccessNotification';
import {
  ConfigurationLoadingState,
  useConfiguration,
} from '../ApplicationProviders/ConfigurationProvider/ConfigurationProvider';

import { FlowFinished } from '../screens/FlowFinished/FlowFinished';
import { ProviderEntity } from '../../models/provider';
import { findProviderByName } from '../../utilities/provider-utils';
import { Credentials } from '../../models/credentials';
import { applicationParameters } from '../../utilities/application-parameters';
import { useIsFlowHandledByRedirectHandler } from '../../utilities/useIsFlowHandledByRedirectHandler';
import { messages as credentialsMessages } from './messages';
import { CredentialsResult } from './CredentialsResult';

type CredentialsDirectorProps = PropsFromRedux;

export const CredentialsDirectorPresenter: React.FC<CredentialsDirectorProps> = ({
  credentials,
  children,
  providers,
  redirectedCredentialsId,
}) => {
  const provider = credentials
    ? findProviderByName(providers as ProviderEntity[], credentials.providerName)
    : undefined;

  const configuration = useConfiguration();
  const credentialsId = applicationParameters.get('credentialsId');

  const flowContinuesInRedirectHandler = useIsFlowHandledByRedirectHandler({
    redirectedCredentialsId,
    appUri: applicationParameters.get('appUri'),
    credentials,
  });

  if (configuration.loadingState === ConfigurationLoadingState.LOADING) {
    return <LoadingOverlay />;
  }

  const credentialsUpdated = Credentials.isFinished(credentials, configuration.data);
  const overlayMessage = credentialsId ? (
    <FormattedMessage {...credentialsMessages.credentialsRefreshed} />
  ) : (
    <FormattedMessage
      {...authorizationMessages.accountConnectedTo}
      values={{ providerName: provider?.displayName }}
    />
  );

  if (flowContinuesInRedirectHandler && credentialsUpdated) {
    return <FlowFinished />;
  }

  if (credentialsUpdated) {
    const applicationSuccessResult = new CredentialsResult({
      credentialsId: credentials.id,
    });

    return (
      <SuccessNotification
        overlayHeading={overlayMessage}
        applicationSuccessResult={applicationSuccessResult}
      />
    );
  }

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

const mapStateToProps = (state: AppState) => {
  return {
    providers: state.providers.data,
    credentials: state.credentials.data,
    redirectedCredentialsId: state.application.redirectedCredentialsId,
  };
};

const connector = connect(mapStateToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export const CredentialsDirector = connector(CredentialsDirectorPresenter);
